import { FC, useCallback, useMemo } from 'react';

import { useRouter } from 'next/compat/router';

import { MinimumWorkspace } from '@repo/api-client';
import { Avatar } from '@repo/ui-kit/components/core/Avatars';
import { Badge } from '@repo/ui-kit/components/core/Badge';

import { useWorkspaceStore } from 'stores/workspaceStore';

import {
  SelectableWorkspaceItem,
  WorkspaceItemDetails,
  WorkspaceItemDetailsName,
} from '../styles';
import { toTitleCase } from '../utils';
import { WorkspaceMemberCount } from '../WorkspaceMemberCount';
import { WorkspaceName } from '../WorkspaceName';
const WORKSPACE_PATH_REGEX = /\/workspace\/[^/]+/;
const WORKSPACE_WITH_FOLDER_PATH_REGEX = /\/workspace\/[^/]+\/folder/;

export const WorkspaceSelectorItem: FC<{
  workspace: MinimumWorkspace;
  handleWorkspaceClick: () => void;
}> = ({ workspace, handleWorkspaceClick }) => {
  const { id: workspaceId } = workspace;
  const router = useRouter();

  const currentPath = router?.pathname;

  const isInFolder = useMemo(() => {
    return !!currentPath?.match(WORKSPACE_WITH_FOLDER_PATH_REGEX);
  }, [currentPath]);

  // when we change workspace, we try to keep the current path but replace the
  // workspaceId in the url with the new workspaceId
  // if we are in a folder, we need to return to the root of the workspace
  const newPath = useMemo(() => {
    if (isInFolder) {
      return `/workspace/${workspaceId}/projects`;
    }
    return (
      currentPath?.replace(WORKSPACE_PATH_REGEX, `/workspace/${workspaceId}`) ||
      `/workspace/${workspaceId}`
    );
  }, [currentPath, workspaceId, isInFolder]);

  const setWorkspaceIdInStore = useWorkspaceStore(
    (state) => state.setActiveWorkspaceId
  );

  const onClick = useCallback(() => {
    if (isInFolder) {
      // TODO: refacotr this as part of - https://app.clickup.com/t/86c38fzxz
      // there is a bug when switching workspaces whilst in a folder
      // that the folder appears in the other workspace
      // and its proving difficult to fix due to race conditions and weird store behavior
      // so we just force a reload for now and will fix it properly by moving to react-query
      // for folder data
      window.location.pathname = newPath;
      return;
    }
    setWorkspaceIdInStore(workspaceId);
    handleWorkspaceClick();
    router?.push(newPath);
  }, [
    workspaceId,
    setWorkspaceIdInStore,
    handleWorkspaceClick,
    router,
    newPath,
    isInFolder,
  ]);

  return (
    <SelectableWorkspaceItem
      key={workspace.id}
      data-disable-nprogress={true}
      onClick={onClick}
    >
      <Avatar fallback={workspace.name} round="square" variant="photo" />
      <WorkspaceItemDetails>
        <WorkspaceItemDetailsName>
          <WorkspaceName workspace={workspace} />
          <WorkspaceMemberCount memberCount={workspace.memberCount} />
        </WorkspaceItemDetailsName>
        <div>
          <Badge size="m" text={toTitleCase(workspace.plan)} />
        </div>
      </WorkspaceItemDetails>
    </SelectableWorkspaceItem>
  );
};
